import { feedbackStore, notify, safeActionError } from './notifications';
const reads = new Map<string, Promise<void>>();
export async function optionalBoundedRead<T>(read: PromiseLike<T>, milliseconds = 5000): Promise<T | null> {
  let deadline: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([Promise.resolve(read), new Promise<null>(resolve => { deadline = setTimeout(() => resolve(null), milliseconds); })]);
  } finally { clearTimeout(deadline); }
}
export function assertReadConfirmed(result: unknown) {
  for (const item of Array.isArray(result) ? result : [result]) {
    if (item && typeof item === 'object' && 'error' in item && item.error) throw item.error;
  }
}
/** Explicit user refreshes/downloads only, never query functions or subscriptions. */
export function refreshWithFeedback(label: string, read: () => Promise<unknown>): Promise<void> {
  const key = `${feedbackStore.getEpoch()}:${label}`;
  const existing = reads.get(key); if (existing) return existing;
  const epoch = feedbackStore.getEpoch();
  const id = notify.loading(`Refreshing ${label}…`);
  const deadline = setTimeout(() => {
    if (epoch === feedbackStore.getEpoch()) notify.warning(`${label} refresh not confirmed`, { id, description: 'The read is taking longer than expected. Keep the current data marked unverified until the server responds.' });
  }, 20_000);
  const operation = Promise.resolve().then(read).then(result => {
    assertReadConfirmed(result);
    if (epoch === feedbackStore.getEpoch()) notify.info(`${label} refreshed`, { id });
  }).catch(error => {
    if (epoch === feedbackStore.getEpoch()) notify.error(`${label} unavailable`, { id, description: safeActionError(error) });
  }).finally(() => { clearTimeout(deadline); reads.delete(key); });
  reads.set(key, operation); return operation;
}

export async function openPrivateEvidence(url: () => Promise<string>) {
  // Open synchronously while the click has browser activation; never call a
  // blocked popup a successful download or put its signed URL into a message.
  const target = window.open('about:blank', '_blank');
  if (!target) throw new Error('Popup blocked');
  target.opener = null;
  try {
    const signedUrl = await url();
    if (new URL(signedUrl).protocol !== 'https:') throw new Error('Private evidence unavailable');
    target.location.replace(signedUrl);
  } catch (error) { target.close(); throw error; }
}
